// ─── Gallery: Dark Editorial ──────────────────────────────────
// Asymmetric layout for dark themes: one tall image left,
// stacked smaller images right. Luxe rounded corners.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

export default function GalleryDarkEditorial({ section, data, theme }) {
  const anim = section.animation || {};
  const images = (data.images || []).filter(Boolean);
  const [lightboxImg, setLightboxImg] = useState(null);

  if (images.length === 0) return null;

  return (
    <>
      <section className="px-6 py-20 sm:py-24" data-testid="gallery-section">
        <div className="mx-auto max-w-6xl">
          <AnimatedSection preset="fade-in" className="mb-12 text-center">
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-3"
              style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
            >
              Visuals
            </p>
            <h2
              className="text-4xl sm:text-5xl"
              style={{ fontFamily: theme.headingFont, color: theme.textPrimary }}
            >
              Gallery
            </h2>
          </AnimatedSection>

          <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
            {/* Large image */}
            {images[0] && (
              <AnimatedSection preset={anim.items || 'zoom-soft'}>
                <div
                  className="overflow-hidden rounded-[2rem] cursor-pointer transition-transform hover:scale-[1.01]"
                  onClick={() => setLightboxImg(images[0])}
                >
                  <img
                    src={images[0]}
                    alt="Gallery 1"
                    className="h-[520px] w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </AnimatedSection>
            )}

            {/* Stacked smaller */}
            <div className="grid gap-4">
              {images.slice(1).map((img, i) => (
                <AnimatedSection key={i} preset={anim.items || 'zoom-soft'} delay={i * 0.08}>
                  <div
                    className="overflow-hidden rounded-[1.4rem] cursor-pointer transition-transform hover:scale-[1.01]"
                    onClick={() => setLightboxImg(img)}
                  >
                    <img
                      src={img}
                      alt={`Gallery ${i + 2}`}
                      className="h-[160px] w-full object-cover sm:h-[200px]"
                      loading="lazy"
                    />
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setLightboxImg(null)}
        >
          <motion.img
            src={lightboxImg}
            alt="Gallery photo enlarged"
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
          />
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white text-2xl"
            onClick={() => setLightboxImg(null)}
            aria-label="Close gallery lightbox"
          >
            &times;
          </button>
        </motion.div>
      )}
    </>
  );
}
